import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { navigationItems } from '../data/mock';

const Header = () => {
  const { language, toggleLanguage } = useLanguage();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0d1f1f]/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="text-2xl font-bold text-white tracking-tight">
              <span className="text-white">THE</span>
              <span className="text-emerald-400 ml-1">WALLNESS</span>
            </div>
            <div className="h-6 w-1 bg-emerald-400 group-hover:h-8 transition-all duration-300"></div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className="text-gray-300 hover:text-emerald-400 transition-colors duration-200 text-sm font-medium"
              >
                {language === 'en' ? item.label_en : item.label_es}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 text-sm text-gray-300 hover:text-white transition-colors"
            >
              <img
                src={language === 'en' ? 'https://flagcdn.com/w20/gb.png' : 'https://flagcdn.com/w20/es.png'}
                alt={language === 'en' ? 'English' : 'Español'}
                className="w-5 h-3 object-cover"
              />
              <span>{language === 'en' ? 'EN' : 'ES'}</span>
            </button>

            {isAuthenticated ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-white/10">
                    <User className="w-4 h-4 mr-2" />
                    {user?.profile?.first_name || 'Dashboard'}
                  </Button>
                </Link>
                <Button 
                  onClick={handleLogout}
                  variant="ghost" 
                  className="text-gray-300 hover:text-red-400 hover:bg-red-500/10"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Logout' : 'Salir'}
                </Button>
              </>
            ) : (
              <>
                <Link to="/auth">
                  <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-white/10">
                    {language === 'en' ? 'Login' : 'Iniciar Sesión'}
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium">
                    {language === 'en' ? 'Get Started' : 'Comenzar'}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 space-y-4 border-t border-gray-700 pt-4">
            {navigationItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className="block text-gray-300 hover:text-emerald-400 transition-colors duration-200"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {language === 'en' ? item.label_en : item.label_es}
              </Link>
            ))}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 text-gray-300 hover:text-white"
            >
              <img
                src={language === 'en' ? 'https://flagcdn.com/w20/gb.png' : 'https://flagcdn.com/w20/es.png'}
                alt={language === 'en' ? 'English' : 'Español'}
                className="w-5 h-3 object-cover"
              />
              <span>{language === 'en' ? 'Switch to Spanish' : 'Cambiar a Inglés'}</span>
            </button>
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full border-emerald-500 text-emerald-400">
                    <User className="w-4 h-4 mr-2" />
                    Dashboard
                  </Button>
                </Link>
                <Button 
                  onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                  variant="outline" 
                  className="w-full border-red-500/50 text-red-400"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Logout' : 'Cerrar Sesión'}
                </Button>
              </>
            ) : (
              <Link to="/auth" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600">
                  {language === 'en' ? 'Login / Register' : 'Iniciar Sesión / Registrarse'}
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;